import { useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Button, Error } from 'shared/ui'
import type { IChatbotFullData } from 'entities/Chatbot'
import type { IResponseFilterSegment } from 'entities/Segment'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { ChatbotEnrollModalStore } from 'widgets/ChatbotEnroll/store/ChatbotEnrollModalStore'
import { createChatbotEnrollSearchStore } from 'widgets/ChatbotEnroll/store/ChatbotEnrollSearchStore'
import { SendFrom } from 'widgets/SendFrom'
import { ChatbotEnrollModalFilters } from './ChatbotEnrollModalFilters'
import { ChatbotEnrollModalSearch } from './ChatbotEnrollModalSearch'
import styles from './styles.module.scss'

type IChatbotEnrollModalProps = {
  contactIds?: number[]
  filtersList?: IResponseFilterSegment[]
  search?: string
  bulkAll?: boolean
  onClose: () => void
}

export const ChatbotEnrollModal = observer(
  ({ contactIds, filtersList, search, bulkAll, onClose }: IChatbotEnrollModalProps) => {
    const entities = useEntitiesContext()
    const store = useMemo(() => new ChatbotEnrollModalStore(entities), [])
    const searchStore = useMemo(() => createChatbotEnrollSearchStore(), [])
    const { sendFromStore, error, checkValidate } = store
    const [chatbot, setChatbot] = useState<IChatbotFullData | null>(null)
    const [applyFilters, setApplyFilters] = useState<boolean>(false)
    const [loading, setLoading] = useState(false)

    const enrollDisabled = !chatbot

    const handleChatbotSelect = async (selectedChatbot: IChatbotFullData) => {
      const sendFrom = selectedChatbot.send_from

      setChatbot(selectedChatbot)
      if (sendFrom) void sendFromStore.setSendFrom(sendFrom)
    }

    const handleEnroll = () => {
      if (!chatbot || !checkValidate() || !sendFromStore.paramsSendFrom) return

      setLoading(true)

      if (contactIds) {
        store
          .enrollContacts({
            agentId: chatbot.id,
            contactIds,
            applyFilters,
          })
          .then((success) => {
            setLoading(false)
            if (success) onClose()
          })
      } else {
        store
          .enrollContactsByFilters({
            agentId: chatbot.id,
            inboxId: sendFromStore.inbox_id,
            filtersList,
            applyFilters,
            search,
            bulkAll,
          })
          .then((success) => {
            setLoading(false)
            if (success) onClose()
          })
      }
    }

    return (
      <div className={styles.content}>
        <div className={styles.contentRows}>
          <div className={styles.contentRow}>
            <span className={styles.contentRowTitle}>AI Agent</span>
            <ChatbotEnrollModalSearch onSelect={handleChatbotSelect} searchStore={searchStore} />
          </div>
          {chatbot && (
            <div className={styles.contentRow}>
              <span className={styles.contentRowTitle}>Inbox</span>
              <SendFrom
                store={sendFromStore}
                triggerVariant={'textField'}
                textFiledTriggerProps={{
                  textFieldPlaceholderProps: {
                    variant: 'stroke',
                    size: 'medium',
                    className: styles.textField,
                    errorState: !!error,
                  },
                }}
                onClearError={() => store.setError('')}
              />
              {!!error && <Error type={'text'} error={error} />}
            </div>
          )}
          <div className={styles.contentRow}>
            <ChatbotEnrollModalFilters value={applyFilters} onChange={setApplyFilters} />
          </div>
        </div>
        <div className={styles.contentFooter}>
          <Button
            size='medium'
            text='Cancel'
            contained='secondary'
            disabled={loading}
            onClick={onClose}
          />
          <Button
            size='medium'
            text='Enroll'
            loading={loading}
            disabled={enrollDisabled}
            onClick={handleEnroll}
          />
        </div>
      </div>
    )
  }
)
